// Entity graph review panel — the approval gate.
//
// Extraction proposes; nothing reaches the graph until an editor approves it
// here. Sits in the Article Review right-hand column between Classification
// and Context Cards.
//
// The design rests on one thing: every AI relationship carries a verbatim
// evidence_quote from the article body. Reviewing is reading the quote and
// deciding whether the edge above it is what the sentence says. Everything
// else in this panel exists to make that judgement fast.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/Button";
import { Card, CardSubtle, CardTitle } from "./ui/Card";
import { Input, Label, Select } from "./ui/Input";
import { Modal } from "./ui/Modal";
import { Spinner } from "./ui/Spinner";
import { api, ApiError } from "../lib/api";
import {
  formatValidity,
  parseQualifierValidity,
} from "@globalvillage/shared";

type ReviewStatus = "pending" | "approved" | "rejected" | "superseded";

interface EntityRef {
  id: string;
  name: string;
  entity_type: string;
  iso_code?: string | null;
}

interface CandidateRef {
  id: string;
  raw_name: string;
  suggested_type: string | null;
  status: string;
}

interface Mention {
  id: string;
  mention_text: string;
  salience: "primary" | "secondary";
  confidence: number | null;
  source: "ai" | "editor";
  review_status: ReviewStatus;
  corrected: boolean;
  stale: boolean;
  entity: EntityRef | null;
  candidate: CandidateRef | null;
}

interface Relationship {
  id: string;
  predicate: string;
  mode: string | null;
  qualifier: string | null;
  evidence_quote: string | null;
  confidence: number | null;
  source: "ai" | "editor";
  review_status: ReviewStatus;
  needs_review: boolean;
  corrected: boolean;
  stale: boolean;
  subject: EntityRef | null;
  object: EntityRef | null;
  subject_candidate: { id: string; raw_name: string } | null;
  object_candidate: { id: string; raw_name: string } | null;
  valid_from: string | null;
  valid_to: string | null;
  valid_precision: string;
  valid_source: string;
}

interface Extraction {
  id: string;
  run_number: number;
  status: "running" | "ok" | "error";
  trigger: string;
  model: string | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

interface GraphResponse {
  extraction: Extraction | null;
  /** The article has been edited since the last extraction run. */
  body_stale: boolean;
  counts: {
    pending: number;
    approved: number;
    rejected: number;
    stale: number;
    needs_review: number;
    unresolved: number;
  };
  mentions: Mention[];
  relationships: Relationship[];
}

interface PredicateDef {
  key: string;
  label: string;
  description: string;
  predicate_class: "event" | "structural";
  modes: string[];
}

interface ValidTermDef {
  key: string;
  label: string;
  description: string;
}

/**
 * Valid time an editor has staged for a row but not yet saved. Held in the
 * panel rather than the row so it survives the row re-rendering, and so the
 * correct-relationship modal can carry it through instead of dropping it.
 */
interface ValidityDraft {
  valid_from: string | null;
  valid_to: string | null;
  valid_precision: string;
  valid_source: string;
}

interface ErrorClassDef {
  key: string;
  label: string;
  description: string;
  targets: Array<"mention" | "relationship">;
}

/**
 * What the reject modal is currently asking about.
 *
 * Rejection only. Retracting a stale edge stays a single click and carries no
 * class: that edge was right when it was approved, and the body moved out from
 * under it. Labelling it as a model error would poison the corpus with
 * successes.
 */
type RejectTarget = {
  kind: "mention" | "relationship";
  id: string;
  summary: string;
  quote?: string | null;
};

interface Props {
  articleId: string;
  /** Bumped by the parent when the body changes, to re-check staleness. */
  refreshKey?: number;
}

export function EntityGraphPanel({ articleId, refreshKey }: Props) {
  const [data, setData] = useState<GraphResponse | null>(null);
  const [predicates, setPredicates] = useState<PredicateDef[]>([]);
  const [errorClasses, setErrorClasses] = useState<ErrorClassDef[]>([]);
  const [precisions, setPrecisions] = useState<ValidTermDef[]>([]);
  const [validSources, setValidSources] = useState<ValidTermDef[]>([]);
  const [validity, setValidity] = useState<Record<string, ValidityDraft>>({});
  const [datingRel, setDatingRel] = useState<Relationship | null>(null);
  const [rejecting, setRejecting] = useState<RejectTarget | null>(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [showApproved, setShowApproved] = useState(false);
  const [correcting, setCorrecting] = useState<Relationship | null>(null);
  const [correctingMention, setCorrectingMention] = useState<Mention | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      const r = await api<GraphResponse>(
        `/news/editor/articles/${articleId}/graph`,
      );
      setData(r);
      setError(null);
    } catch (e: any) {
      setError(e.message ?? "Failed to load the entity graph");
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    api<{
      predicates: PredicateDef[];
      error_classes: ErrorClassDef[];
      valid_precisions: ValidTermDef[];
      valid_sources: ValidTermDef[];
    }>("/news/editor/graph/predicates")
      .then((r) => {
        setPredicates(r.predicates);
        setErrorClasses(r.error_classes ?? []);
        setPrecisions(r.valid_precisions ?? []);
        setValidSources(r.valid_sources ?? []);
      })
      .catch(() => {
        setPredicates([]);
        setErrorClasses([]);
        setPrecisions([]);
        setValidSources([]);
      });
  }, []);

  // A run can be orphaned by an API restart; the server reports it as an
  // error after ten minutes. Until then, poll so the panel fills in by itself.
  useEffect(() => {
    if (data?.extraction?.status !== "running") return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [data?.extraction?.status, load]);

  async function runExtraction() {
    setExtracting(true);
    setError(null);
    try {
      await api(`/news/editor/articles/${articleId}/extract`, {
        method: "POST",
        body: { force: true },
      });
      await load();
    } catch (e: any) {
      setError(
        e instanceof ApiError && e.status === 429
          ? "Claude is rate limited. Try again in a few minutes."
          : (e.message ?? "Extraction failed"),
      );
    } finally {
      setExtracting(false);
    }
  }

  async function reviewMention(
    id: string,
    action: "approve" | "reject",
    label?: { error_class: string; review_note: string | null },
  ): Promise<void> {
    setBusyRow(id);
    try {
      await api(`/news/editor/graph/mentions/${id}/review`, {
        method: "POST",
        body: { action, ...label },
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not update that mention");
    } finally {
      setBusyRow(null);
    }
  }

  async function reviewRelationship(
    id: string,
    action: "approve" | "reject" | "keep" | "retract",
    label?: { error_class: string; review_note: string | null },
  ): Promise<void> {
    setBusyRow(id);
    try {
      // The staged dates ride along with the approval rather than needing a
      // second save — one click still finishes the row.
      const dates =
        action === "approve" || action === "keep" ? validity[id] : undefined;
      await api(`/news/editor/graph/relationships/${id}/review`, {
        method: "POST",
        body: { action, ...label, ...dates },
      });
      setValidity((v) => {
        const { [id]: _drop, ...rest } = v;
        return rest;
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not update that relationship");
    } finally {
      setBusyRow(null);
    }
  }

  async function approveAll() {
    setBusyRow("__all__");
    try {
      // Bulk approve goes straight to the server and knows nothing about dates
      // staged in this panel. Sending those rows individually FIRST is what
      // stops the button from quietly discarding work the editor has just
      // done — the alternative is a click that looks like it succeeded and
      // silently drops every date.
      const staged = Object.entries(validity);
      for (const [id, dates] of staged) {
        await api(`/news/editor/graph/relationships/${id}/review`, {
          method: "POST",
          body: { action: "approve", ...dates },
        });
      }
      if (staged.length) setValidity({});

      const r = await api<{ mentions: number; relationships: number }>(
        `/news/editor/articles/${articleId}/graph/approve-all`,
        { method: "POST" },
      );
      setError(null);
      await load();
      if (r.mentions === 0 && r.relationships === 0 && !staged.length) {
        setError(
          "Nothing was approved — everything pending needs an individual decision.",
        );
      }
    } catch (e: any) {
      setError(e.message ?? "Bulk approve failed");
    } finally {
      setBusyRow(null);
    }
  }

  const mentions = data?.mentions ?? [];
  const relationships = data?.relationships ?? [];
  const visible = (s: ReviewStatus) => showApproved || s === "pending";

  const pendingMentions = mentions.filter((m) => visible(m.review_status));
  const pendingRels = relationships.filter(
    (r) => visible(r.review_status) && !r.stale,
  );
  const staleRels = relationships.filter(
    (r) => r.stale && r.review_status === "approved",
  );

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={styles.header}>
        <CardTitle style={{ marginBottom: 0 }}>
          Entities &amp; relationships
        </CardTitle>
        <Button
          variant="secondary"
          size="sm"
          onClick={runExtraction}
          disabled={extracting || data?.extraction?.status === "running"}
        >
          {extracting || data?.extraction?.status === "running"
            ? "Extracting…"
            : data?.extraction
              ? "Re-extract"
              : "Extract"}
        </Button>
      </div>

      <CardSubtle style={{ marginBottom: 10 }}>
        <ExtractionStatus
          extraction={data?.extraction ?? null}
          counts={data?.counts}
        />
      </CardSubtle>

      {data?.body_stale ? (
        <CardSubtle style={styles.staleBanner}>
          The article has been edited since this run — these edges reflect the
          older text. Re-extract when the wording is settled.
        </CardSubtle>
      ) : null}

      {error ? (
        <CardSubtle style={{ color: "var(--danger)", marginBottom: 10 }}>
          {error}
        </CardSubtle>
      ) : null}

      {loading ? (
        <div style={{ padding: 12, textAlign: "center" }}>
          <Spinner size={16} />
        </div>
      ) : !data?.extraction ? (
        <CardSubtle>
          Not extracted yet. Extraction runs automatically when the article is
          published, or press Extract to see it now.
        </CardSubtle>
      ) : (
        <>
          {/* ── entities ─────────────────────────────────────────── */}
          <SectionLabel>
            Entities {mentions.length ? <Count>{mentions.length}</Count> : null}
          </SectionLabel>
          {pendingMentions.length === 0 ? (
            <CardSubtle>
              {mentions.length ? "All reviewed." : "None found."}
            </CardSubtle>
          ) : (
            pendingMentions.map((m) => (
              <div key={m.id} style={styles.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowTitle}>
                    {m.entity ? (
                      <>
                        {m.entity.name}
                        <Pill tone="muted">{m.entity.entity_type}</Pill>
                      </>
                    ) : (
                      <>
                        {m.candidate?.raw_name ?? m.mention_text}
                        <Pill tone="warn">unresolved</Pill>
                      </>
                    )}
                    {m.salience === "primary" ? (
                      <Pill tone="accent">primary</Pill>
                    ) : null}
                    <StatusPill
                      status={m.review_status}
                      corrected={m.corrected}
                    />
                  </div>
                  <CardSubtle>
                    “{m.mention_text}”
                    {m.confidence != null
                      ? ` · ${(m.confidence * 100).toFixed(0)}%`
                      : ""}
                    {!m.entity ? (
                      <>
                        {" · "}
                        <Link to="/graph" style={styles.link}>
                          review in the entity queue
                        </Link>
                      </>
                    ) : null}
                  </CardSubtle>
                </div>
                {m.review_status === "pending" ? (
                  <RowActions
                    busy={busyRow === m.id}
                    onApprove={
                      m.entity
                        ? () => reviewMention(m.id, "approve")
                        : undefined
                    }
                    approveTitle={
                      m.entity
                        ? "Approve"
                        : "Point it at the right entity, or promote it from the queue"
                    }
                    onCorrect={() => setCorrectingMention(m)}
                    onReject={() =>
                      setRejecting({
                        kind: "mention",
                        id: m.id,
                        summary:
                          m.entity?.name ??
                          m.candidate?.raw_name ??
                          m.mention_text,
                        quote: m.mention_text,
                      })
                    }
                  />
                ) : null}
              </div>
            ))
          )}

          {/* ── relationships ────────────────────────────────────── */}
          <SectionLabel style={{ marginTop: 14 }}>
            Relationships{" "}
            {relationships.length ? (
              <Count>{relationships.length}</Count>
            ) : null}
          </SectionLabel>
          {pendingRels.length === 0 ? (
            <CardSubtle>
              {relationships.length ? "All reviewed." : "None found."}
            </CardSubtle>
          ) : (
            pendingRels.map((r) => (
              <RelationshipRow
                key={r.id}
                rel={r}
                predicates={predicates}
                busy={busyRow === r.id}
                draft={validity[r.id]}
                onDraft={(d) =>
                  setValidity((v) =>
                    d ? { ...v, [r.id]: d } : omitKey(v, r.id),
                  )
                }
                onOpenDates={() => setDatingRel(r)}
                onApprove={() => reviewRelationship(r.id, "approve")}
                onReject={() =>
                  setRejecting({
                    kind: "relationship",
                    id: r.id,
                    summary: describeEdge(r),
                    quote: r.evidence_quote,
                  })
                }
                onCorrect={() => setCorrecting(r)}
              />
            ))
          )}

          {/* ── stale ────────────────────────────────────────────── */}
          {staleRels.length > 0 ? (
            <>
              <SectionLabel style={{ marginTop: 14, color: "var(--danger)" }}>
                No longer supported by the body{" "}
                <Count>{staleRels.length}</Count>
              </SectionLabel>
              <CardSubtle style={{ marginBottom: 6 }}>
                These were approved earlier but the current text no longer says
                them. They are still in the graph until you decide.
              </CardSubtle>
              {staleRels.map((r) => (
                <RelationshipRow
                  key={r.id}
                  rel={r}
                  predicates={predicates}
                  busy={busyRow === r.id}
                  approveLabel="Keep"
                  rejectLabel="Retract"
                  onApprove={() => reviewRelationship(r.id, "keep")}
                  onReject={() => reviewRelationship(r.id, "retract")}
                  onCorrect={() => setCorrecting(r)}
                />
              ))}
            </>
          ) : null}

          <div style={styles.footer}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowApproved((v) => !v)}
            >
              {showApproved ? "Hide reviewed" : "Show all"}
            </Button>
            {(data.counts.pending ?? 0) > 0 ? (
              <Button
                size="sm"
                onClick={approveAll}
                disabled={busyRow === "__all__"}
                title="Approves only fully-resolved rows that are not flagged"
              >
                {busyRow === "__all__"
                  ? "Approving…"
                  : `Approve ${data.counts.pending} clear`}
              </Button>
            ) : null}
          </div>
        </>
      )}

      {correctingMention ? (
        <CorrectMentionModal
          mention={correctingMention}
          onClose={() => setCorrectingMention(null)}
          onSaved={async () => {
            setCorrectingMention(null);
            await load();
          }}
        />
      ) : null}

      {correcting ? (
        <CorrectRelationshipModal
          rel={correcting}
          predicates={predicates}
          errorClasses={errorClasses.filter((ec) =>
            ec.targets.includes("relationship"),
          )}
          validity={validity[correcting.id]}
          onClose={() => setCorrecting(null)}
          onSaved={async () => {
            setCorrecting(null);
            await load();
          }}
        />
      ) : null}

      {datingRel ? (
        <ValidityModal
          rel={datingRel}
          precisions={precisions}
          sources={validSources}
          draft={validity[datingRel.id]}
          onClose={() => setDatingRel(null)}
          onSave={(d) => {
            const id = datingRel.id;
            setValidity((v) => (d ? { ...v, [id]: d } : omitKey(v, id)));
            setDatingRel(null);
          }}
        />
      ) : null}

      {rejecting ? (
        <RejectModal
          target={rejecting}
          errorClasses={errorClasses.filter((e) =>
            e.targets.includes(rejecting.kind),
          )}
          onClose={() => setRejecting(null)}
          onConfirm={async (error_class, review_note) => {
            const t = rejecting;
            setRejecting(null);
            if (t.kind === "mention") {
              await reviewMention(t.id, "reject", { error_class, review_note });
            } else {
              await reviewRelationship(t.id, "reject", {
                error_class,
                review_note,
              });
            }
          }}
        />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function ExtractionStatus({
  extraction,
  counts,
}: {
  extraction: Extraction | null;
  counts?: GraphResponse["counts"];
}) {
  if (!extraction) return <>Not yet extracted.</>;
  if (extraction.status === "running")
    return <>Run {extraction.run_number} in progress…</>;
  if (extraction.status === "error")
    return (
      <span style={{ color: "var(--danger)" }}>
        Run {extraction.run_number} failed:{" "}
        {extraction.error ?? "unknown error"}
      </span>
    );

  const bits = [
    `Run ${extraction.run_number}`,
    extraction.model ?? null,
    counts ? `${counts.approved} approved, ${counts.pending} pending` : null,
    counts?.needs_review ? `${counts.needs_review} flagged` : null,
    counts?.unresolved ? `${counts.unresolved} unresolved` : null,
    extraction.finished_at ? relative(extraction.finished_at) : null,
  ].filter(Boolean);
  return <>{bits.join(" · ")}</>;
}

function RelationshipRow({
  rel,
  busy,
  draft,
  onDraft,
  onOpenDates,
  onApprove,
  onReject,
  onCorrect,
  approveLabel,
  rejectLabel,
}: {
  rel: Relationship;
  predicates: PredicateDef[];
  busy: boolean;
  draft?: ValidityDraft;
  onDraft?: (d: ValidityDraft | null) => void;
  onOpenDates?: () => void;
  onApprove: () => void;
  onReject: () => void;
  onCorrect: () => void;
  approveLabel?: string;
  rejectLabel?: string;
}) {
  const subject = rel.subject?.name ?? rel.subject_candidate?.raw_name ?? "?";
  const object = rel.object?.name ?? rel.object_candidate?.raw_name ?? "?";
  const unresolved = !rel.subject || !rel.object;

  return (
    <div style={styles.row}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.rowTitle}>
          <span>{subject}</span>
          <span style={styles.predicate}>
            {rel.predicate}
            {rel.mode ? `·${rel.mode}` : ""}
          </span>
          <span>{object}</span>
          {rel.needs_review ? <Pill tone="warn">flagged</Pill> : null}
          {unresolved ? <Pill tone="warn">unresolved</Pill> : null}
          <StatusPill status={rel.review_status} corrected={rel.corrected} />
        </div>
        {rel.qualifier ? (
          <CardSubtle style={{ color: "var(--text-secondary)" }}>
            {rel.qualifier}
          </CardSubtle>
        ) : null}
        {rel.evidence_quote ? (
          <div style={styles.quote}>“{rel.evidence_quote}”</div>
        ) : (
          <CardSubtle style={{ fontStyle: "italic" }}>
            Added by an editor — no quote.
          </CardSubtle>
        )}
        {onDraft && onOpenDates ? (
          <ValidityStrip
            rel={rel}
            draft={draft}
            onDraft={onDraft}
            onOpenDates={onOpenDates}
          />
        ) : null}
      </div>
      <RowActions
        busy={busy}
        onApprove={unresolved ? undefined : onApprove}
        approveTitle={
          unresolved
            ? "Resolve both endpoints in the entity queue first"
            : (approveLabel ?? "Approve")
        }
        approveLabel={approveLabel}
        onReject={onReject}
        rejectLabel={rejectLabel}
        onCorrect={onCorrect}
      />
    </div>
  );
}

function RowActions({
  busy,
  onApprove,
  onReject,
  onCorrect,
  approveTitle,
  approveLabel,
  rejectLabel,
}: {
  busy: boolean;
  onApprove?: () => void;
  onReject: () => void;
  onCorrect?: () => void;
  approveTitle?: string;
  approveLabel?: string;
  rejectLabel?: string;
}) {
  if (busy)
    return (
      <div style={styles.actions}>
        <Spinner size={14} />
      </div>
    );
  return (
    <div style={styles.actions}>
      <button
        onClick={onApprove}
        disabled={!onApprove}
        title={approveTitle ?? "Approve"}
        style={{
          ...styles.iconBtn,
          color: onApprove ? "var(--success)" : "var(--text-muted)",
          cursor: onApprove ? "pointer" : "not-allowed",
        }}
      >
        {approveLabel ?? "✓"}
      </button>
      {onCorrect ? (
        <button onClick={onCorrect} title="Correct" style={styles.iconBtn}>
          ✎
        </button>
      ) : null}
      <button
        onClick={onReject}
        title={rejectLabel ?? "Reject"}
        style={{ ...styles.iconBtn, color: "var(--danger)" }}
      >
        {rejectLabel ?? "✕"}
      </button>
    </div>
  );
}

function CorrectRelationshipModal({
  rel,
  predicates,
  errorClasses,
  validity,
  onClose,
  onSaved,
}: {
  rel: Relationship;
  predicates: PredicateDef[];
  errorClasses: ErrorClassDef[];
  /** Staged in the row before the editor opened this modal — carried through
   *  rather than dropped, so correcting an edge does not discard its dates. */
  validity?: ValidityDraft;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [predicate, setPredicate] = useState(rel.predicate);
  const [mode, setMode] = useState(rel.mode ?? "");
  const [qualifier, setQualifier] = useState(rel.qualifier ?? "");
  // wrong_predicate by default, but the two cases worth counting separately
  // hide inside it: swapping "aid" for "disapprove/allege" is an attribution
  // failure, and swapping it for "reject/deny" is a missed negation. The same
  // edit, a different lesson — so this is asked, not inferred.
  const [errorClass, setErrorClass] = useState("wrong_predicate");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const def = predicates.find((p) => p.key === predicate);
  const events = predicates.filter((p) => p.predicate_class === "event");
  const structural = predicates.filter(
    (p) => p.predicate_class === "structural",
  );

  // Modes belong to a predicate; changing the predicate invalidates the mode.
  useEffect(() => {
    if (mode && def && !def.modes.includes(mode)) setMode("");
  }, [predicate, def, mode]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await api(`/news/editor/graph/relationships/${rel.id}/review`, {
        method: "POST",
        body: {
          action: "correct",
          predicate,
          mode: mode || null,
          qualifier: qualifier.trim() || null,
          error_class: errorClass,
          review_note: null,
          ...validity,
        },
      });
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? "Could not save the correction");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Correct relationship" width={620}>
      <div style={styles.quote}>“{rel.evidence_quote ?? "(no quote)"}”</div>

      <div style={{ margin: "12px 0", fontWeight: 600 }}>
        {rel.subject?.name ?? rel.subject_candidate?.raw_name} →{" "}
        {rel.object?.name ?? rel.object_candidate?.raw_name}
      </div>

      <Label>Predicate</Label>
      <Select value={predicate} onChange={(e) => setPredicate(e.target.value)}>
        <optgroup label="Events — something that happened">
          {events.map((p) => (
            <option key={p.key} value={p.key}>
              {p.key} — {p.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Standing facts">
          {structural.map((p) => (
            <option key={p.key} value={p.key}>
              {p.key} — {p.label}
            </option>
          ))}
        </optgroup>
      </Select>
      {def ? (
        <CardSubtle style={{ marginTop: 4 }}>{def.description}</CardSubtle>
      ) : null}

      {def && def.modes.length > 0 ? (
        <>
          <Label style={{ marginTop: 12 }}>Mode</Label>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="">(none)</option>
            {def.modes.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </>
      ) : null}

      <Label style={{ marginTop: 12 }}>
        Qualifier
        {predicate === "disapprove" || predicate === "reject"
          ? " — what was alleged or denied"
          : ""}
      </Label>
      <Input
        value={qualifier}
        onChange={(e) => setQualifier(e.target.value)}
        placeholder={
          predicate === "disapprove"
            ? "of arming the Rapid Support Forces"
            : "since the 2021 coup"
        }
      />
      {(predicate === "disapprove" || predicate === "reject") &&
      !qualifier.trim() ? (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 4 }}>
          Without this, the edge records that a dispute happened and nothing
          about its substance.
        </CardSubtle>
      ) : null}

      <Label style={{ marginTop: 12 }}>What did the model get wrong?</Label>
      <Select
        value={errorClass}
        onChange={(ev) => setErrorClass(ev.target.value)}
      >
        {errorClasses.map((ec) => (
          <option key={ec.key} value={ec.key}>
            {ec.label}
          </option>
        ))}
      </Select>
      <CardSubtle style={{ marginTop: 4 }}>
        {errorClasses.find((ec) => ec.key === errorClass)?.description ?? ""}
      </CardSubtle>

      {err ? (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 10 }}>
          {err}
        </CardSubtle>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save & approve"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

function CorrectMentionModal({
  mention,
  onClose,
  onSaved,
}: {
  mention: Mention;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<EntityRef[]>([]);
  const [chosen, setChosen] = useState<EntityRef | null>(null);
  const [addAlias, setAddAlias] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api<{ entities: EntityRef[] }>(
        `/news/editor/graph/entities?limit=8&q=${encodeURIComponent(q.trim())}`,
      )
        .then((r) => setResults(r.entities))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  async function save() {
    if (!chosen) return;
    setSaving(true);
    setErr(null);
    try {
      await api(`/news/editor/graph/mentions/${mention.id}/review`, {
        method: "POST",
        body: {
          action: "correct",
          entity_id: chosen.id,
          add_alias: addAlias,
          // Repointing a mention at a different entity IS wrong_entity —
          // there is no second answer worth asking for.
          error_class: "wrong_entity",
          review_note: null,
        },
      });
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? "Could not save the correction");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Point this mention at the right entity"
      width={560}
    >
      <CardSubtle>
        The article says “{mention.mention_text}”
        {mention.entity ? (
          <>
            , currently resolved to <strong>{mention.entity.name}</strong>.
          </>
        ) : (
          ", which did not resolve to anything."
        )}
      </CardSubtle>

      <Label style={{ marginTop: 12 }}>Search entities and aliases</Label>
      <Input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setChosen(null);
        }}
        placeholder="try an alias: usa, drc, saf…"
        autoFocus
      />

      <div style={{ marginTop: 8 }}>
        {results.map((r) => (
          <button
            key={r.id}
            onClick={() => setChosen(r)}
            style={{
              ...styles.pickRow,
              borderColor:
                chosen?.id === r.id ? "var(--accent)" : "var(--border)",
            }}
          >
            <span style={{ fontWeight: 600 }}>{r.name}</span>
            <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
              {r.entity_type}
            </span>
          </button>
        ))}
        {q.trim() && results.length === 0 ? (
          <CardSubtle>
            No match. If this is a new entity, promote it from the{" "}
            <Link to="/graph" style={styles.link}>
              candidate queue
            </Link>{" "}
            instead.
          </CardSubtle>
        ) : null}
      </div>

      {chosen ? (
        <label style={styles.checkRow}>
          <input
            type="checkbox"
            checked={addAlias}
            onChange={(e) => setAddAlias(e.target.checked)}
          />
          <span>
            Also record “{mention.mention_text}” as an alias of {chosen.name}
            <CardSubtle>
              Only if it is genuinely another name for it. A generic phrase like
              “the bloc” must never become an alias — it would silently capture
              every future article that uses those words.
            </CardSubtle>
          </span>
        </label>
      ) : null}

      {err ? (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 10 }}>
          {err}
        </CardSubtle>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button onClick={save} disabled={!chosen || saving}>
          {saving ? "Saving…" : "Save & approve"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

// ── local primitives (no shared Badge/Pill exists in this app) ─────────────

function SectionLabel({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return <div style={{ ...styles.sectionLabel, ...style }}>{children}</div>;
}

function Count({ children }: { children: React.ReactNode }) {
  return <span style={styles.count}>{children}</span>;
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "muted" | "warn" | "accent" | "ok";
}) {
  const tones: Record<string, React.CSSProperties> = {
    muted: { background: "var(--surface-hover)", color: "var(--text-muted)" },
    warn: { background: "rgba(245,158,11,0.15)", color: "var(--accent)" },
    accent: { background: "rgba(245,158,11,0.15)", color: "var(--accent)" },
    ok: { background: "rgba(16,185,129,0.15)", color: "var(--success)" },
  };
  return <span style={{ ...styles.pill, ...tones[tone] }}>{children}</span>;
}

function StatusPill({
  status,
  corrected,
}: {
  status: ReviewStatus;
  corrected: boolean;
}) {
  if (status === "pending") return null;
  if (status === "approved")
    return <Pill tone="ok">{corrected ? "corrected" : "approved"}</Pill>;
  if (status === "rejected") return <Pill tone="muted">rejected</Pill>;
  return null;
}

function relative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** "Egypt —sanction/blockade→ Sudan", for the reject modal's header. */
function describeEdge(r: Relationship): string {
  const subj = r.subject?.name ?? r.subject_candidate?.raw_name ?? "?";
  const obj = r.object?.name ?? r.object_candidate?.raw_name ?? "?";
  const pred = r.mode ? `${r.predicate}/${r.mode}` : r.predicate;
  return `${subj} —${pred}→ ${obj}`;
}

// Rejecting is one click; classifying the rejection is a second. That second
// click is the whole reason this modal exists — a "rejected" bit records that
// the model was wrong, and nothing about how, which is the part worth keeping.
// news_entity_relationships.proposed already stores what the model said; this
// stores what kind of mistake it was.
function RejectModal({
  target,
  errorClasses,
  onClose,
  onConfirm,
}: {
  target: RejectTarget;
  errorClasses: ErrorClassDef[];
  onClose: () => void;
  onConfirm: (errorClass: string, note: string | null) => Promise<void>;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // "Other" without a note is the one combination that records nothing: it
  // says the list is incomplete without saying what is missing.
  const needsNote = chosen === "other" && !note.trim();
  const canSave = !!chosen && !needsNote && !saving;

  return (
    <Modal open onClose={onClose} title="What was wrong?" width={560}>
      {target.quote ? (
        <div style={styles.quote}>“{target.quote}”</div>
      ) : null}
      <div style={{ fontWeight: 600, margin: "10px 0 14px" }}>
        {target.summary}
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        {errorClasses.map((e) => {
          const on = chosen === e.key;
          return (
            <button
              key={e.key}
              type="button"
              onClick={() => setChosen(e.key)}
              style={{
                textAlign: "left",
                padding: "8px 10px",
                borderRadius: "var(--radius-sm)",
                border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                background: on ? "var(--surface-hover)" : "transparent",
                color: "var(--text-primary)",
                cursor: "pointer",
                font: "inherit",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{e.label}</div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-muted)",
                  marginTop: 2,
                }}
              >
                {e.description}
              </div>
            </button>
          );
        })}
      </div>

      <Label style={{ marginTop: 14 }}>
        Note {chosen === "other" ? "— required" : "(optional)"}
      </Label>
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={
          chosen === "other"
            ? "What kind of wrong was it? A run of these is how the list grows."
            : "Anything the class does not capture"
        }
      />

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button
          onClick={async () => {
            if (!chosen) return;
            setSaving(true);
            try {
              await onConfirm(chosen, note.trim() || null);
            } finally {
              setSaving(false);
            }
          }}
          disabled={!canSave}
        >
          {saving ? "Saving\u2026" : "Reject"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

function omitKey<T>(o: Record<string, T>, k: string): Record<string, T> {
  const { [k]: _drop, ...rest } = o;
  return rest;
}

/**
 * Valid time for one relationship: what is stored, what the editor has staged,
 * or what the qualifier suggests.
 *
 * The suggestion is offered, never applied. A parser that silently filled the
 * field would put a machine-derived date behind a human's approval click,
 * which is the one thing the review gate exists to prevent — so accepting it
 * costs a deliberate click and lands as 'stated', while anything typed by hand
 * lands as 'editor'.
 */
function ValidityStrip({
  rel,
  draft,
  onDraft,
  onOpenDates,
}: {
  rel: Relationship;
  draft?: ValidityDraft;
  onDraft: (d: ValidityDraft | null) => void;
  onOpenDates: () => void;
}) {
  const suggestion = useMemo(
    () => parseQualifierValidity(rel.qualifier),
    [rel.qualifier],
  );

  const stored =
    rel.valid_source !== "unknown" && (rel.valid_from || rel.valid_to)
      ? formatValidity(rel.valid_from, rel.valid_to, rel.valid_precision)
      : null;

  const staged = draft
    ? formatValidity(draft.valid_from, draft.valid_to, draft.valid_precision)
    : null;

  const shown = staged ?? stored;

  return (
    <div style={styles.validRow}>
      <span style={styles.validIcon} aria-hidden="true">
        ◷
      </span>
      {shown ? (
        <>
          <span style={styles.validPill}>{shown}</span>
          {staged ? (
            <button
              type="button"
              onClick={() => onDraft(null)}
              style={styles.validLink}
              title="Discard these dates"
            >
              clear
            </button>
          ) : null}
          <button type="button" onClick={onOpenDates} style={styles.validLink}>
            edit
          </button>
        </>
      ) : suggestion ? (
        <>
          <button
            type="button"
            onClick={() =>
              onDraft({
                valid_from: suggestion.validFrom,
                valid_to: suggestion.validTo,
                valid_precision: suggestion.precision,
                valid_source: "stated",
              })
            }
            style={styles.validSuggest}
            title={`Read from the qualifier: “${suggestion.matched}”`}
          >
            {formatValidity(
              suggestion.validFrom,
              suggestion.validTo,
              suggestion.precision,
            )}
            <span style={{ opacity: 0.7 }}> — use this?</span>
          </button>
          <button type="button" onClick={onOpenDates} style={styles.validLink}>
            or set
          </button>
        </>
      ) : (
        <button type="button" onClick={onOpenDates} style={styles.validLink}>
          add dates
        </button>
      )}
    </div>
  );
}

/** Manual entry. The precision selector is the point of the whole form. */
function ValidityModal({
  rel,
  precisions,
  sources,
  draft,
  onClose,
  onSave,
}: {
  rel: Relationship;
  precisions: ValidTermDef[];
  sources: ValidTermDef[];
  draft?: ValidityDraft;
  onClose: () => void;
  onSave: (d: ValidityDraft | null) => void;
}) {
  const initial: ValidityDraft = draft ?? {
    valid_from: rel.valid_from,
    valid_to: rel.valid_to,
    valid_precision:
      rel.valid_precision === "unknown" ? "year" : rel.valid_precision,
    valid_source: rel.valid_source === "unknown" ? "editor" : rel.valid_source,
  };
  const [from, setFrom] = useState(initial.valid_from ?? "");
  const [to, setTo] = useState(initial.valid_to ?? "");
  const [precision, setPrecision] = useState(initial.valid_precision);
  const [source, setSource] = useState(initial.valid_source);
  // Once the editor picks a precision themselves, stop second-guessing them.
  const [precisionTouched, setPrecisionTouched] = useState(!!draft);

  // A date input forces a full date even when the article only said "2021", so
  // the editor has to pad it — and the padding is legible. 1 January reads as a
  // year they rounded; the 1st of some other month reads as a month; anything
  // else reads as a date they actually knew. Guessing "year" for all three
  // would quietly discard a day the editor did know, which is the same
  // fabrication as inventing one, pointed the other way.
  useEffect(() => {
    if (precisionTouched || !from) return;
    const [, mm, dd] = from.split("-");
    setPrecision(
      dd !== "01" ? "day" : mm === "01" ? "year" : "month",
    );
  }, [from, precisionTouched]);

  const dated = !!from || !!to;
  const outOfOrder = !!from && !!to && from > to;
  const canSave = !outOfOrder;

  return (
    <Modal open onClose={onClose} title="When was this true?" width={560}>
      <div style={styles.quote}>“{rel.evidence_quote ?? "(no quote)"}”</div>
      {rel.qualifier ? (
        <CardSubtle style={{ marginTop: 8 }}>
          Qualifier: {rel.qualifier}
        </CardSubtle>
      ) : null}

      <CardSubtle style={{ marginTop: 12 }}>
        This is when the assertion was true in the world — not when we recorded
        it. Leave both empty if the article does not say.
      </CardSubtle>

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1 }}>
          <Label>From</Label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Until</Label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>
      <CardSubtle style={{ marginTop: 4 }}>
        An empty “until” means the article gives no end — still true as far as
        it says. That is different from not knowing.
      </CardSubtle>

      {outOfOrder ? (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 8 }}>
          The end precedes the start.
        </CardSubtle>
      ) : null}

      {dated ? (
        <>
          <Label style={{ marginTop: 12 }}>How precise is that, really?</Label>
          <Select
            value={precision}
            onChange={(e) => {
              setPrecisionTouched(true);
              setPrecision(e.target.value);
            }}
          >
            {precisions
              .filter((p) => p.key !== "unknown")
              .map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
          </Select>
          <CardSubtle style={{ marginTop: 4 }}>
            {precisions.find((p) => p.key === precision)?.description ?? ""}
          </CardSubtle>

          <Label style={{ marginTop: 12 }}>Where did it come from?</Label>
          <Select value={source} onChange={(e) => setSource(e.target.value)}>
            {sources
              .filter((x) => x.key !== "unknown")
              .map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
          </Select>
          <CardSubtle style={{ marginTop: 4 }}>
            {sources.find((x) => x.key === source)?.description ?? ""}
          </CardSubtle>
        </>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button
          onClick={() =>
            onSave(
              dated
                ? {
                    valid_from: from || null,
                    valid_to: to || null,
                    valid_precision: precision,
                    valid_source: source,
                  }
                : null,
            )
          }
          disabled={!canSave}
        >
          {dated ? "Stage these dates" : "Leave undated"}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
      <CardSubtle style={{ marginTop: 10 }}>
        Saved when you approve the relationship.
      </CardSubtle>
    </Modal>
  );
}

const styles: Record<string, React.CSSProperties> = {
  validRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
    fontSize: 12,
  },
  validIcon: {
    color: "var(--text-muted)",
    fontSize: 13,
    lineHeight: 1,
  },
  validPill: {
    color: "var(--text-primary)",
    background: "var(--surface-hover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    padding: "1px 6px",
    fontVariantNumeric: "tabular-nums",
  },
  // Deliberately reads as a question, not as a value already applied.
  validSuggest: {
    color: "var(--accent)",
    background: "transparent",
    border: "1px dashed var(--accent)",
    borderRadius: "var(--radius-sm)",
    padding: "1px 6px",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
  },
  validLink: {
    color: "var(--text-muted)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
    gap: 8,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "var(--text-secondary)",
    margin: "10px 0 6px",
  },
  count: {
    fontWeight: 400,
    color: "var(--text-muted)",
  },
  row: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    padding: "8px 0",
    borderTop: "1px solid var(--border)",
  },
  rowTitle: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
  },
  predicate: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    color: "var(--accent)",
    padding: "1px 6px",
    borderRadius: "var(--radius-sm)",
    background: "var(--surface-hover)",
  },
  quote: {
    fontSize: 12,
    fontStyle: "italic",
    color: "var(--text-muted)",
    borderLeft: "2px solid var(--border)",
    paddingLeft: 8,
    marginTop: 4,
  },
  actions: { display: "flex", gap: 2, flexShrink: 0 },
  iconBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-secondary)",
    cursor: "pointer",
    fontSize: 13,
    lineHeight: 1,
    padding: "4px 7px",
  },
  pill: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    padding: "1px 5px",
    borderRadius: 3,
  },
  footer: {
    display: "flex",
    gap: 8,
    marginTop: 12,
    paddingTop: 10,
    borderTop: "1px solid var(--border)",
  },
  link: { color: "var(--accent)" },
  staleBanner: {
    background: "rgba(245,158,11,0.1)",
    border: "1px solid rgba(245,158,11,0.3)",
    borderRadius: "var(--radius-sm)",
    color: "var(--accent)",
    marginBottom: 10,
    padding: "6px 9px",
  },
  pickRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    background: "var(--surface-hover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-primary)",
    cursor: "pointer",
    marginBottom: 4,
    padding: "7px 10px",
    textAlign: "left",
  },
  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 12,
    fontSize: 13,
    cursor: "pointer",
  },
};

export default EntityGraphPanel;
